import { INVENTORIZATION_BLOCK_IDS } from '@/features/inventorization/domain/inventorization';
import {
  getInventorizationContextBlockStatus,
  validateInventorizationContext,
} from '@/features/inventorization/domain/inventorization-context-validation';
import { type CaptureSession, PHOTO_CONTEXTS } from '@/shared/capture/capture-session';

export {
  getInventorizationContextBlockStatus,
  validateInventorizationContext,
} from '@/features/inventorization/domain/inventorization-context-validation';

export const INVENTORIZATION_CONTEXT_SOURCES = {
  manual: 'manual',
  sameAsPrevious: 'same_as_previous',
  workPack: 'work_pack',
} as const;

export type InventorizationContextSource =
  | ''
  | (typeof INVENTORIZATION_CONTEXT_SOURCES)[keyof typeof INVENTORIZATION_CONTEXT_SOURCES];

export interface InventorizationContext {
  buildingName: string;
  floorName: string;
  locationName: string;
  ownerName: string;
  source: InventorizationContextSource;
  tenantName: string;
  workPackId: string;
}

export function readInventorizationContext(session: CaptureSession): InventorizationContext {
  const inventorization = session.data.inventorization as { context?: Partial<InventorizationContext> } | undefined;
  const context = inventorization?.context ?? {};

  return {
    buildingName: context.buildingName ?? '',
    floorName: context.floorName ?? '',
    locationName: context.locationName ?? '',
    ownerName: context.ownerName ?? '',
    source: readContextSource(context.source),
    tenantName: context.tenantName ?? '',
    workPackId: context.workPackId ?? '',
  };
}

export function updateInventorizationContext(
  session: CaptureSession,
  patch: Partial<InventorizationContext>,
  updatedAt: string,
): CaptureSession {
  const context = {
    ...readInventorizationContext(session),
    ...patch,
  };
  const hasPhoto = hasInventorizationContextPhoto(session);
  const issues = validateInventorizationContext(context);
  const contextStatus = getInventorizationContextBlockStatus(context, hasPhoto);

  return {
    ...session,
    blockStatuses: {
      ...session.blockStatuses,
      [INVENTORIZATION_BLOCK_IDS.context]: contextStatus,
    },
    data: {
      ...session.data,
      inventorization: {
        ...(session.data.inventorization as object),
        context,
      },
    },
    updatedAt,
    validationErrors: [
      ...session.validationErrors.filter(issue => issue.blockId !== INVENTORIZATION_BLOCK_IDS.context),
      ...issues,
    ],
  };
}

export function hasInventorizationContextPhoto(session: CaptureSession) {
  return session.photos.some(photo => photo.context === PHOTO_CONTEXTS.context);
}

function readContextSource(value: unknown): InventorizationContextSource {
  if (
    value === INVENTORIZATION_CONTEXT_SOURCES.workPack ||
    value === INVENTORIZATION_CONTEXT_SOURCES.manual ||
    value === INVENTORIZATION_CONTEXT_SOURCES.sameAsPrevious
  ) {
    return value;
  }

  return '';
}
